import { type ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Field,
  Modal,
  Textarea,
  Typography
} from '@matillion/component-library'
import ConflictsAlert from '../ConflictsAlert/ConflictsAlert'

import BranchSelector from 'components/BranchSelector/BranchSelector'
import CurrentBranchDisplay from 'components/CurrentBranchDisplay/CurrentBranchDisplay'
import ModalTitle from 'components/ModalTitle'
import useMergeAction from 'hooks/useMergeAction/useMergeAction'
import MergeArrowIcon from 'icons/MergeArrow.svg'
import classes from './MergeIntoBranchModal.module.scss'

import BranchSelectorLabel from 'components/BranchSelectorLabel/BranchSelectorLabel'
import ConflictResolutionTable from 'components/ConflictResolutionTable/ConflictResolutionTable'
import PublishJobsForm from 'components/PublishJobsForm/PublishJobsForm'
import useGitContext from 'provider/GitProvider/useGitContext'
import {
  type MergeBranchSelectorProps,
  type MergeIntoBranchModalProps
} from './types'
import useResolveConflicts from 'hooks/useResolveConflicts'
import { queryKeys } from 'api/query-keys'
import { type PublishToggleEvent } from '../PublishJobsForm/types'
import { useQueryClient } from '@tanstack/react-query'
import useDescribeMergeDecision from '../../hooks/useDescribeMergeDecision/useDescribeMergeDecision'

const MergeBranchSelector = ({
  branch,
  branches,
  isInvalidating,
  setBranch
}: MergeBranchSelectorProps) => (
  <div className={classes.MergeModalDialog__MergeBranchSelector}>
    <div className={classes.MergeModalDialog__MergeBranchSelector_Branch}>
      <BranchSelectorLabel
        prefixKey="merge.sourceBranch.prefix"
        suffixKey="merge.sourceBranch.suffix"
      />

      <CurrentBranchDisplay />
    </div>
    <div
      className={
        classes.MergeModalDialog__MergeBranchSelector_ColumnBetweenBranches
      }
    >
      <img src={MergeArrowIcon} alt={'Merge Arrow Icon'} />
    </div>
    <div className={classes.MergeModalDialog__MergeBranchSelector_Branch}>
      <BranchSelectorLabel
        prefixKey="merge.targetBranch.prefix"
        suffixKey="merge.targetBranch.suffix"
      />
      <BranchSelector
        branch={branch}
        branches={branches}
        loading={isInvalidating}
        onSelectBranch={setBranch}
        excludeSourceBranch={true}
      />
    </div>
  </div>
)

export const MergeIntoBranchModal = ({
  onClose,
  branches,
  environments,
  onPublishPipelines
}: MergeIntoBranchModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { branch, project } = useGitContext()

  const [isInvalidating, setIsInvalidating] = useState(false)
  const [targetBranch, setTargetBranch] = useState<string | undefined>()
  const [mergeCommitMessage, setMergeCommitMessage] = useState<
    string | undefined
  >('')

  const {
    shouldShowPublishForm,
    isLegacyDescribeLoadingAndNotIdle,
    isLoading: isDescribeLoading,
    mergeDescription
  } = useDescribeMergeDecision({ targetBranch })

  const { sourceCommitId, targetCommitId, conflicts } = mergeDescription
  const hasConflicts = !!conflicts && conflicts.length > 0
  const { selections, hasResolvedConflicts, updateSelection } =
    useResolveConflicts(conflicts)

  const [publish, setPublish] = useState(false)
  const [environment, setEnvironment] = useState<string | undefined>(undefined)
  const [envsUnavailable, setEnvsUnavailable] = useState(false)
  const publishAvailable = targetBranch === 'main'

  const handleMergeSuccess = useCallback(
    async (commitId?: string) => {
      if (targetBranch && environment && commitId) {
        await onPublishPipelines({
          commitId,
          environment,
          branch: targetBranch
        })
      }

      onClose()
    },
    [onClose, onPublishPipelines, targetBranch, environment]
  )

  const {
    isValid: isMergeValid,
    isLoading: isMergeLoading,
    merge
  } = useMergeAction({
    conflictSelections: selections,
    mergeMessage: mergeCommitMessage,
    sourceCommitId,
    targetCommitId,
    targetBranch,
    onSuccess: handleMergeSuccess,
    onFailure: onClose
  })

  const onMergeCommitMessageChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setMergeCommitMessage(e.target.value)
    },
    []
  )

  const handleEnvsUnavailable = useCallback(() => {
    setPublish(false)
    setEnvsUnavailable(true)
  }, [setEnvsUnavailable])

  const handlePublishFormChange = useCallback(
    ({ enabled, environment: newEnvironment }: PublishToggleEvent) => {
      setPublish(enabled)
      setEnvironment(newEnvironment)
    },
    [setPublish, setEnvironment]
  )

  useEffect(() => {
    if (targetBranch) {
      setMergeCommitMessage(`Merge from [${branch}] in to [${targetBranch}]`)

      if (!publishAvailable) {
        setPublish(false)
      }
    }
  }, [branch, conflicts, targetBranch, publishAvailable, setPublish])

  const isLoading = isMergeLoading
  const isPublishValid = environment && !envsUnavailable
  const cannotMerge =
    !isMergeValid ||
    (publish && !isPublishValid) ||
    (hasConflicts && !hasResolvedConflicts) ||
    isDescribeLoading

  useEffect(() => {
    setIsInvalidating(true)
    queryClient
      .invalidateQueries({
        queryKey: [queryKeys.legacyMerge],
        fetchStatus: 'idle'
      })
      .then(() => {
        setIsInvalidating(false)
      })
  }, [targetBranch, project, queryClient])

  return (
    <Modal
      size="mid"
      onCancel={onClose}
      id="merge-dialog"
      disableBackdropClick
      ariaLabelledBy="merge-title"
      className={classes.MergeModalDialog__Dialog}
    >
      <ModalTitle
        titleId={'merge-title'}
        title={t('merge.modal.title')}
        description={t('merge.modal.subtitle')}
      />

      <MergeBranchSelector
        branches={branches}
        branch={targetBranch}
        setBranch={setTargetBranch}
        isInvalidating={isInvalidating}
      />

      {targetBranch && (
        <>
          {!isLegacyDescribeLoadingAndNotIdle && (
            <ConflictsAlert
              hasConflicts={hasConflicts}
              hasResolvedConflicts={hasResolvedConflicts}
            />
          )}

          {hasConflicts && (
            <>
              <p className={classes.MergeModalDialog__ConflictsLabel}>
                <Typography format="bcm" weight="bold" as="span">
                  {t('merge.conflicts')}
                </Typography>{' '}
                <Typography format="bcm" as="span">
                  ({conflicts.length})
                </Typography>
              </p>

              <ConflictResolutionTable
                selections={selections}
                onVersionSelection={updateSelection}
              />
            </>
          )}

          <Field
            id="merge-commit-message"
            inputComponent={Textarea}
            value={mergeCommitMessage}
            data-testid="commit-message-text"
            title={t('merge.commitMessage')}
            onChange={onMergeCommitMessageChange}
            aria-label={t('merge.commitMessage')}
            disabled={isMergeLoading || isLegacyDescribeLoadingAndNotIdle}
          />

          {shouldShowPublishForm && (
            <div className={classes.MergeModalDialog__PublishContainer}>
              <PublishJobsForm
                environments={environments}
                unavailable={!publishAvailable}
                onChange={handlePublishFormChange}
                onUnavailable={handleEnvsUnavailable}
                warningKey={'publish.message.not-default-target-branch'}
              />
            </div>
          )}
        </>
      )}

      <div className={classes.MergeModalDialog__Buttons}>
        <Button
          id="cancel"
          alt="secondary"
          onClick={onClose}
          text={t('cancel')}
          data-testid="cancel-merge-button"
        />

        <Button
          onClick={merge}
          id="perform-merge"
          waiting={isLoading}
          disabled={cannotMerge}
          text={t('merge.merge')}
          data-testid="modal-merge-submit"
          alt="positive"
        />
      </div>
    </Modal>
  )
}

export default MergeIntoBranchModal
